import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import type { Flow } from "../types/types";
import { loadUserTemplates, UserTemplate } from "../storage/templateStorage";
import { auth } from "../firebase";
import { useStore } from "../App/store";
import "../styles/components/PropertiesDialog.css";

interface PropertiesDialogProps {
  visible: boolean;
  onHide: () => void;
  flowData: {
    id: string;
    name: string;
    email: string;
    role: string;
    favourite: boolean;
    template?: string;
    templateRules?: any;
    nodeType?: string;
  } | null;
  onSave: (updates: Partial<any>) => void;
}

const PropertiesDialog: React.FC<PropertiesDialogProps> = ({
  visible,
  onHide,
  flowData,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [favourite, setFavourite] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(
    undefined,
  );
  const [userTemplates, setUserTemplates] = useState<UserTemplate[]>([]);

  // Load user templates when dialog opens
  useEffect(() => {
    const loadTemplates = async () => {
      const user = auth.currentUser;
      if (user && user.uid) {
        const templates = await loadUserTemplates(
          user.uid,
          useStore.getState().useLocalStorage,
        );
        setUserTemplates(templates);
      }
    };
    if (visible) {
      loadTemplates();
    }
  }, [visible]);

  // Load system templates
  const systemTemplates = useMemo(() => {
    const modules = import.meta.glob("../Templates/*.md", {
      query: "?raw",
      import: "default",
      eager: true,
    }) as Record<string, string>;

    const extractFirstH1 = (markdown: string): string | null => {
      const match = markdown.match(/^#\s+(.+?)\s*$/m);
      return match ? match[1].trim() : null;
    };

    const extractTemplateRules = (markdown: string): any => {
      const yamlMatch = markdown.match(/```ya?ml\n([\s\S]*?)\n```/);
      if (!yamlMatch) return null;

      try {
        // Parse YAML (basic implementation - you might want to use a proper YAML parser)
        const yamlContent = yamlMatch[1];
        // For now, store the raw YAML content
        return yamlContent;
      } catch (error) {
        console.error("Error parsing template rules:", error);
        return null;
      }
    };

    const extractNodeType = (markdown: string): string | null => {
      const match = markdown.match(/^NodeType:\s*(.+?)\s*$/m);
      return match ? match[1].trim() : null;
    };

    const list = Object.entries(modules)
      .map(([filePath, markdown]) => {
        const file = filePath.split("/").pop() || filePath;
        const id = file.replace(/\.md$/i, "");
        const lowerId = id.toLowerCase();
        const isDocOnly =
          lowerId === "readme" ||
          lowerId === "template_authoring" ||
          lowerId === "template-authoring" ||
          lowerId.startsWith("_");
        if (isDocOnly) return null;

        const name = extractFirstH1(markdown) || id;
        const rules = extractTemplateRules(markdown);
        const nodeType = extractNodeType(markdown);
        return { id, name, rules, nodeType };
      })
      .filter(Boolean) as Array<{
      id: string;
      name: string;
      rules: any;
      nodeType: string | null;
    }>;

    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, []);

  // Merge system and user templates for dropdown options
  const allTemplates = useMemo(() => {
    const system = systemTemplates.map((t) => ({
      label: t.name,
      value: t.id,
      isSystem: true,
    }));
    const user = userTemplates.map((t) => ({
      label: t.name,
      value: t.templateId,
      isSystem: false,
    }));
    return [...system, ...user];
  }, [systemTemplates, userTemplates]);

  // Initialize form with current flow data
  useEffect(() => {
    if (flowData) {
      setName(flowData.name || "");
      setEmail(flowData.email || "");
      setRole(flowData.role || "");
      setFavourite(flowData.favourite || false);
      setSelectedTemplate(flowData.template);
    }
  }, [flowData]);

  const handleSave = () => {
    const updates: Partial<Flow> = {
      name,
      email,
      role,
      favourite,
      template: selectedTemplate,
    };

    // If template changed, load the template rules
    if (selectedTemplate && selectedTemplate !== flowData?.template) {
      // First check system templates
      const systemTemplate = systemTemplates.find(
        (t) => t.id === selectedTemplate,
      );
      if (systemTemplate) {
        if (systemTemplate.rules) {
          updates.templateRules = systemTemplate.rules;
        }
        if (systemTemplate.nodeType) {
          updates.nodeType = systemTemplate.nodeType;
        }
      } else {
        // Check user templates
        const userTemplate = userTemplates.find(
          (t) => t.templateId === selectedTemplate,
        );
        if (userTemplate) {
          // Extract rules from markdown
          const yamlMatch = userTemplate.markdown.match(
            /```ya?ml\n([\s\S]*?)\n```/,
          );
          if (yamlMatch) {
            updates.templateRules = yamlMatch[1];
          }
          if (userTemplate.nodeType) {
            updates.nodeType = userTemplate.nodeType;
          }
        }
      }
    }

    onSave(updates);
    onHide();
  };

  const templateOptions = [
    { label: "No Template", value: undefined, isSystem: true },
    ...allTemplates,
  ];

  // Custom template option renderer to show badges for system/user templates
  const templateOptionTemplate = (option: any) => {
    if (!option.value) {
      return <span>{option.label}</span>;
    }
    return (
      <div className="template-option">
        <span>{option.label}</span>
        {option.isSystem ? (
          <span className="template-option-badge">System</span>
        ) : (
          <span className="template-option-badge user">User</span>
        )}
      </div>
    );
  };

  return (
    <Dialog
      header="Map Properties"
      visible={visible}
      style={{ width: "550px" }}
      onHide={onHide}
      className="properties-dialog"
      footer={
        <div>
          <Button label="Cancel" icon="pi pi-times" onClick={onHide} text />
          <Button label="Save" icon="pi pi-check" onClick={handleSave} />
        </div>
      }
    >
      <div className="properties-dialog-content">
        <div className="properties-dialog-field">
          <label htmlFor="name">Name</label>
          <InputText
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="properties-dialog-field">
          <label htmlFor="email">Email</label>
          <InputText
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="properties-dialog-field">
          <label htmlFor="role">Role</label>
          <InputText
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          />
        </div>

        <div className="properties-dialog-field-checkbox">
          <Checkbox
            inputId="favourite"
            checked={favourite}
            onChange={(e) => setFavourite(e.checked ?? false)}
          />
          <label htmlFor="favourite">Favourite</label>
        </div>

        <div className="properties-dialog-field">
          <label htmlFor="template">Template</label>
          <Dropdown
            id="template"
            value={selectedTemplate}
            options={templateOptions}
            onChange={(e) => setSelectedTemplate(e.value)}
            placeholder="Select a template"
            itemTemplate={templateOptionTemplate}
            filter
            filterPlaceholder="Search templates"
          />
        </div>
      </div>
    </Dialog>
  );
};

export default PropertiesDialog;
